import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Modal,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  UploadOutlined,
  FileExcelOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import DataTable from "../../components/CustomDatatable/index.js";
import { stringSorter } from "../../utils/functions.js";
import { useDispatch, useSelector } from "react-redux";
import {
  getItems,
  deleteItem,
  updateItem,
  addItem,
  importItemExcel,
} from "../../store/actions/item.js";
import { getCustomers } from "../../store/actions/customers";
import { getPackage } from "../../store/actions/package";
import { getPackageTransaction } from "../../store/actions/packageTransactions.js";
import { getSeries } from "../../store/actions/series.js";
import { getCategories } from "../../store/actions/category.js";
import { getColors } from "../../store/actions/color.js";
import { getBrands } from "../../store/actions/brand.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import ItemForm from "./ItemForm.js";
import { toast } from "react-toastify";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const Items = () => {
  document.title = "Items";
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Excel import state ────────────────────────────────────────────────────
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const dispatch = useDispatch();

  const items = useSelector((state) => state?.item?.list ?? []);
  const totalCount = useSelector((state) => state?.item?.totalCount ?? 0);
  const seriesList = useSelector((state) => state?.series?.list ?? []);
  const categories = useSelector((state) => state?.category?.list ?? []);
  const colors = useSelector((state) => state?.color?.list ?? []);
  const brandList = useSelector((state) => state?.brand?.list ?? []);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({});

  const seriesMap = seriesList.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});
  const categoryMap = categories.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});
  const colorMap = colors.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});

  // ── Fetch items ────────────────────────────────────────────────────────────
  const getData = useCallback(async (pageNumber = 1, search = "", currentFilters = {}) => {
    setIsLoading(true);
    await dispatch(getItems({ page: pageNumber, search, ...currentFilters }));
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    getData(currentPage, searchText, filters);
  }, [currentPage, searchText, filters, getData]);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getPackage());
    dispatch(getPackageTransaction());
    dispatch(getSeries());
    dispatch(getCategories());
    dispatch(getColors());
    dispatch(getBrands());
  }, [dispatch]);

  // ── ADD / UPDATE ──────────────────────────────────────────────────────────
  const handleCreate = useCallback(
    async (values) => {
      if (selectedRecord?.id) {
        const res = await dispatch(
          updateItem({
            id: selectedRecord.id,
            ...values,
            seriesId: values.seriesId ? values.seriesId : undefined,
            categoryid: values.categoryid ? values.categoryid : undefined,
            colorId: values.colorId ? values.colorId : undefined,
          }),
        );
        if (res?.code === 1) getData(currentPage);
      } else {
        const res = await dispatch(addItem(values));
        if (res?.code === 1) getData(currentPage);
      }
      setIsModalVisible(false);
      setSelectedRecord(null);
    },
    [dispatch, selectedRecord?.id, getData, currentPage],
  );

  // ── DELETE ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (id) => {
      const res = await dispatch(deleteItem(id));
      if (res?.code === 1) getData(currentPage);
    },
    [dispatch, getData, currentPage],
  );

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const renderCell = (text) => (text ? text : "N/A");

  // ── EXCEL IMPORT ──────────────────────────────────────────────────────────
  const openImportModal = () => {
    setExcelFile(null);
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setExcelFile(null);
  };

  /**
   * Ant Design Upload beforeUpload — returning false prevents auto-upload.
   * We validate the file type here and store it in state.
   */
  const beforeUpload = (file) => {
    const isExcel =
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");

    if (!isExcel) {
      message.error("Only .xlsx or .xls files are allowed.");
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File must be smaller than 10 MB.");
      return Upload.LIST_IGNORE;
    }

    setExcelFile(file);
    return false; // prevent auto-upload
  };

  const handleRemoveFile = () => {
    setExcelFile(null);
  };

  const handleDownloadDemoExcel = () => {
    const link = document.createElement("a");
    // Dynamically calculate the base URL path (works for both local and production, subfolder or root)
    const pathname = window.location.pathname;
    const itemsIndex = pathname.lastIndexOf("/items");
    let basePath = "";
    if (itemsIndex !== -1) {
      basePath = pathname.substring(0, itemsIndex);
    }
    // Clean up trailing slashes from basePath
    if (basePath.endsWith("/")) {
      basePath = basePath.slice(0, -1);
    }
    link.href = basePath + "/Sample/SuperAdmin-Item-Import.xlsx";
    link.download = "SuperAdmin-Item-Import.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportExcel = async () => {
    if (!excelFile) {
      toast.warning("Please select an Excel file first.");
      return;
    }

    try {
      setIsImporting(true);
      const formData = new FormData();
      formData.append("excel", excelFile);

      const res = await dispatch(importItemExcel(formData));

      if (res) {
        if (res?.skippedData?.length > 0) {
          res.skippedData.forEach((data) => {
            toast.error(`${data.errorMessage} — Item: ${data.NAME}`);
          });
        } else {
          toast.success("Excel imported successfully!");
        }
        await getData(currentPage);
      }

      closeImportModal();
    } catch (error) {
      toast.error(error?.message || "Import failed. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  // ── TABLE COLUMNS ─────────────────────────────────────────────────────────
  let columns = [
    { title: "Name", dataIndex: "name", key: "name", sorter: stringSorter("name"), sortDirections: ["ascend", "descend"] },
    { title: "Series", dataIndex: "seriesId", key: "seriesId", sorter: stringSorter("seriesId"), sortDirections: ["ascend", "descend"], render: (id) => seriesMap[id] || renderCell(null) },
    { title: "Category", dataIndex: "categoryid", key: "categoryid", sorter: stringSorter("categoryid"), sortDirections: ["ascend", "descend"], render: (id) => categoryMap[id] || renderCell(null) },
    { title: "Color", dataIndex: "colorId", key: "colorId", sorter: stringSorter("colorId"), sortDirections: ["ascend", "descend"], render: (id) => colorMap[id] || renderCell(null) },
    { title: "Front Camera", dataIndex: "frontCamera", key: "frontCamera", sorter: stringSorter("frontCamera"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Rear Camera", dataIndex: "rearCamera", key: "rearCamera", sorter: stringSorter("rearCamera"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Processor", dataIndex: "processor", key: "processor", sorter: stringSorter("processor"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Weight", dataIndex: "weight", key: "weight", sorter: stringSorter("weight"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Length", dataIndex: "length", key: "length", sorter: stringSorter("length"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Height", dataIndex: "height", key: "height", sorter: stringSorter("height"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Display", dataIndex: "display", key: "display", sorter: stringSorter("display"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "MM Size", dataIndex: "mmSize", key: "mmSize", sorter: stringSorter("mmSize"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Battery", dataIndex: "battery", key: "battery", sorter: stringSorter("battery"), sortDirections: ["ascend", "descend"], render: renderCell },
    { title: "Display Type", dataIndex: "displayType", key: "displayType", sorter: stringSorter("displayType"), sortDirections: ["ascend", "descend"], render: renderCell },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  columns = enhanceColumnsWithFilters(columns);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Items</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>Items</Title>
            </Col>
            <Col>
              <Space>
                {/* ── Import Excel button ─────────────────────────────── */}
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={openImportModal}
                  style={{ borderColor: "#52c41a", color: "#52c41a" }}
                >
                  Import Excel
                </Button>

                {/* ── Add Item button ─────────────────────────────────── */}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedRecord(null);
                    setIsModalVisible(true);
                  }}
                >
                  Add Item
                </Button>
              </Space>
            </Col>
          </Row>

            <DataTable
              dataSource={items.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
              backendPagination={true}
              totalRecords={totalCount}
              page={currentPage}
              limit={20}
              onPageChange={setCurrentPage}
              onSearch={(text) => {
                setSearchText(text);
                setCurrentPage(1);
              }}
              onFilterChange={(newFilters) => {
                const cleaned = {};
                if (newFilters.brandid !== undefined) cleaned.brandid = newFilters.brandid;
                if (newFilters.categoryid !== undefined) cleaned.categoryid = newFilters.categoryid;
                if (newFilters.seriesId !== undefined) cleaned.seriesId = newFilters.seriesId;
                setFilters(cleaned);
                setCurrentPage(1);
              }}
              filterBy={[
                {
                  label: "Brand",
                  field: "brandid",
                  fieldType: "SELECT",
                  options: brandList.map((brand) => ({ id: brand.id, name: brand.name })),
                },
                {
                  label: "Category",
                  field: "categoryid",
                  fieldType: "SELECT",
                  options: categories.map((cat) => ({ id: cat.id, name: cat.name })),
                },
                {
                  label: "Series",
                  field: "seriesId",
                  fieldType: "SELECT",
                  options: seriesList.map((ser) => ({ id: ser.id, name: ser.name })),
                },
              ]}
              isActive={false}
              loading={isLoading}
            />
        </Space>
      </Card>

      {/* ── Item add/edit modal ──────────────────────────────────────────── */}
      <ItemForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />

      {/* ── Excel Import Modal ───────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <FileExcelOutlined style={{ color: "#52c41a", fontSize: 18 }} />
            <span>Import Items from Excel</span>
          </Space>
        }
        open={isImportModalOpen}
        onCancel={closeImportModal}
        footer={[
          <Button key="cancel" onClick={closeImportModal} disabled={isImporting}>
            Cancel
          </Button>,
          <Button
            key="import"
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleImportExcel}
            loading={isImporting}
            disabled={!excelFile}
          >
            {isImporting ? "Importing..." : "Import"}
          </Button>,
        ]}
        width={520}
        destroyOnClose
        zIndex={100000}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Sample template download card */}
          <div
            style={{
              background: "#f9f9f9",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div>
              <Text strong style={{ fontSize: "14px", display: "block" }}>
                Need a sample Excel format?
              </Text>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Download a template file with the required headers and sample data.
              </Text>
            </div>
            <Button
              type="default"
              icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
              onClick={handleDownloadDemoExcel}
              style={{
                width: "100%",
                borderColor: "#52c41a",
                color: "#237804",
                fontWeight: "500",
                background: "#f6ffed",
              }}
            >
              Download Sample Excel
            </Button>
          </div>

          {/* Drag-and-drop / click-to-upload zone */}
          <Dragger
            name="excel"
            multiple={false}
            beforeUpload={beforeUpload}
            onRemove={handleRemoveFile}
            fileList={excelFile ? [excelFile] : []}
            accept=".xlsx,.xls"
            showUploadList={false}   // we render our own file pill below
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#52c41a", fontSize: 40 }} />
            </p>
            <p className="ant-upload-text">
              Click or drag an Excel file here to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              Supports <strong>.xlsx</strong> and <strong>.xls</strong> files up to 10 MB.
            </p>
          </Dragger>

          {/* Selected file preview pill */}
          {excelFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 6,
                padding: "8px 12px",
              }}
            >
              <Space>
                <FileExcelOutlined style={{ color: "#52c41a", fontSize: 18 }} />
                <div>
                  <Text strong style={{ display: "block", lineHeight: 1.3 }}>
                    {excelFile.name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {(excelFile.size / 1024).toFixed(1)} KB
                  </Text>
                </div>
              </Space>
              <Button
                type="text"
                danger
                size="small"
                onClick={handleRemoveFile}
                title="Remove file"
              >
                ✕
              </Button>
            </div>
          )}

          {/* Helper note */}
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>Note:</strong> Rows with invalid or duplicate data will be
            skipped and reported after import. Successfully imported rows will
            appear in the table immediately.
          </Text>
        </Space>
      </Modal>
    </div>
  );
};

export default Items;